import { FunctionComponent, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Check, Minus, Pencil, RefreshCw, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils/ui";
import type { AiChatMessage, AiToolCall } from "@/lib/ai/types";

interface ToolCallItemProps {
  call: AiToolCall;
  exerciseNames: Record<string, string>;
}

const ToolCallItem: FunctionComponent<ToolCallItemProps> = ({
  call,
  exerciseNames,
}) => {
  if (call.name === "add_exercise") {
    const name = exerciseNames[call.args.exercise_id] ?? "Unknown exercise";
    return (
      <div className="flex items-center gap-2 text-xs text-foreground/70">
        <Check className="size-3 flex-shrink-0 text-emerald-500 dark:text-emerald-400" />
        <span>Added {name}</span>
      </div>
    );
  }

  if (call.name === "remove_exercise") {
    const exName = call.args._exerciseName;
    return (
      <div className="flex items-center gap-2 text-xs text-foreground/70">
        <Minus className="size-3 flex-shrink-0 text-red-500 dark:text-red-400" />
        <span>{exName ? `Removed ${exName}` : `Removed exercise at position ${call.args.ex_order + 1}`}</span>
      </div>
    );
  }

  if (call.name === "replace_exercise") {
    const newName = exerciseNames[call.args.exercise_id] ?? "Unknown exercise";
    const oldName = call.args._oldName;
    return (
      <div className="flex items-center gap-2 text-xs text-foreground/70">
        <RefreshCw className="size-3 flex-shrink-0 text-blue-500 dark:text-blue-400" />
        <span>
          {oldName ? `Replaced ${oldName} with ${newName}` : `Replaced position ${call.args.ex_order + 1} with ${newName}`}
        </span>
      </div>
    );
  }

  if (call.name === "update_exercise") {
    const exName = call.args._exerciseName;
    return (
      <div className="flex items-center gap-2 text-xs text-foreground/70">
        <Pencil className="size-3 flex-shrink-0 text-orange-500 dark:text-orange-400" />
        <span>{exName ? `Updated ${exName}` : `Updated position ${call.args.ex_order + 1}`}</span>
      </div>
    );
  }

  return null;
};

const AiAvatar = ({ streaming = false }: { streaming?: boolean }) => {
  if (streaming) {
    return (
      <div className="relative size-9 flex-shrink-0 mt-0.5">
        {/* Wide soft glow via radial mask on conic gradient — no blur, no overflow */}
        <div
          className="absolute inset-0 rounded-full animate-avatar-spin opacity-90"
          style={{
            background:
              "conic-gradient(from 0deg, #8b5cf6, #3b82f6, #06b6d4, #10b981, #8b5cf6)",
            WebkitMask:
              "radial-gradient(circle, transparent 20%, rgba(0,0,0,0.35) 42%, black 58%, rgba(0,0,0,0.15) 80%, transparent 100%)",
            mask:
              "radial-gradient(circle, transparent 20%, rgba(0,0,0,0.35) 42%, black 58%, rgba(0,0,0,0.15) 80%, transparent 100%)",
          }}
        />
        {/* Avatar content */}
        <div className="absolute inset-[4px] rounded-full bg-primary/10 flex items-center justify-center">
          <Sparkles className="size-3.5 text-primary animate-sparkle-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="size-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
      <Sparkles className="size-3.5 text-primary" />
    </div>
  );
};

interface AiChatMessageProps {
  message: AiChatMessage;
  exerciseNames: Record<string, string>;
}

const AiChatMessageComponent: FunctionComponent<AiChatMessageProps> = ({
  message,
  exerciseNames,
}) => {
  const isUser = message.role === "user";

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-3.5 py-2.5 text-sm">
          {message.content}
        </div>
      </div>
    );
  }

  const hasText = Boolean(message.content);
  const hasCalls = message.toolCalls && message.toolCalls.length > 0;

  return (
    <div className="flex gap-2 items-start">
      <AiAvatar />
      <div className="max-w-[85%] bg-muted text-foreground rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-sm">
        {hasText && (
          <div
            className={cn(
              "prose prose-sm dark:prose-invert max-w-none",
              hasCalls && "mb-2.5",
            )}
          >
            <ReactMarkdown
              components={{
                p: ({ children }) => (
                  <p className="mb-1 last:mb-0">{children}</p>
                ),
                ul: ({ children }) => (
                  <ul className="mb-1 pl-4 list-disc">{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className="mb-1 pl-4 list-decimal">{children}</ol>
                ),
                li: ({ children }) => <li className="mb-0.5">{children}</li>,
                strong: ({ children }) => (
                  <strong className="font-semibold">{children}</strong>
                ),
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        )}
        {hasCalls && (
          <div className={cn("flex flex-col gap-1.5", hasText && "border-t border-border/50 pt-2.5")}>
            {message.toolCalls!.map((call, i) => (
              <ToolCallItem
                key={i}
                call={call}
                exerciseNames={exerciseNames}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const THINKING_MESSAGES = [
  "Scanning your exercise library",
  "Checking muscle balance",
  "Picking compound movements",
  "Balancing push/pull ratio",
  "Considering rep ranges",
  "Analysing movement patterns",
  "Optimising exercise order",
  "Checking for overlapping muscles",
  "Selecting the right equipment",
  "Designing your warm-up sequence",
  "Calculating volume load",
  "Reviewing antagonist pairings",
  "Factoring in recovery demands",
  "Choosing the right tempo",
  "Sequencing for maximum output",
];

interface StreamingBubbleProps {
  text: string;
}

export const StreamingBubble: FunctionComponent<StreamingBubbleProps> = ({
  text,
}) => {
  const [thinkingIndex, setThinkingIndex] = useState(
    () => Math.floor(Math.random() * THINKING_MESSAGES.length),
  );
  const [phase, setPhase] = useState<"entering" | "visible" | "exiting">("entering");
  const [dots, setDots] = useState(1);

  useEffect(() => {
    if (text) return;
    const id = setInterval(() => {
      setDots((d) => (d % 3) + 1);
    }, 500);
    return () => clearInterval(id);
  }, [text]);

  useEffect(() => {
    if (text) return;

    setPhase("entering");
    const enterTimer = setTimeout(() => setPhase("visible"), 350);

    const cycleTimer = setInterval(() => {
      setPhase("exiting");
      setTimeout(() => {
        setThinkingIndex((i) => (i + 1) % THINKING_MESSAGES.length);
        setDots(1);
        setPhase("entering");
        setTimeout(() => setPhase("visible"), 350);
      }, 350);
    }, 3500);

    return () => {
      clearTimeout(enterTimer);
      clearInterval(cycleTimer);
    };
  }, [text]);

  const wrapperClass = cn(
    "inline-block transition-[opacity,transform] duration-300 ease-out",
    phase === "entering" && "opacity-0 translate-y-3",
    phase === "visible" && "opacity-100 translate-y-0",
    phase === "exiting" && "opacity-0 -translate-y-3",
  );

  return (
    <div className="flex gap-2 items-start">
      <AiAvatar streaming={!text} />
      <div className="max-w-[85%] bg-muted rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-sm">
        {text ? (
          <span className="text-foreground">{text}</span>
        ) : (
          <span className={wrapperClass}>
            <span
              className="text-xs italic inline-flex items-baseline gap-0 animate-text-shimmer"
              style={{
                backgroundImage:
                  "linear-gradient(90deg, hsl(var(--muted-foreground)) 30%, hsl(var(--foreground)) 50%, hsl(var(--muted-foreground)) 70%)",
                backgroundSize: "200% 100%",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              {THINKING_MESSAGES[thinkingIndex]}
              <span className="inline-block w-[18px] text-left">{".".repeat(dots)}</span>
            </span>
          </span>
        )}
      </div>
    </div>
  );
};

export default AiChatMessageComponent;
